import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, X, Crown, Zap, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA from '../components/WaitlistCTA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const navigate = useNavigate();
  const [founderBilling, setFounderBilling] = useState('monthly');
  const [investorBilling, setInvestorBilling] = useState('monthly');
  const [cofounderBilling, setCofounderBilling] = useState('monthly');
  const [allPlans, setAllPlans] = useState(null);
  const [loading, setLoading] = useState(true);

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  useEffect(() => {
    fetchAllPlans();
  }, []);

  const fetchAllPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/available-plans`);
      const plans = response.data.plans;
      
      const organized = {
        founder: {
          free: plans.find(p => p.plan_name === 'Founder Free'),
          pro: plans.find(p => p.plan_name === 'Founder Pro'),
          elite: plans.find(p => p.plan_name === 'Founder Elite'),
        },
        investor: {
          free: plans.find(p => p.plan_name === 'Investor Free'),
          pro: plans.find(p => p.plan_name === 'Investor Pro'),
          premium: plans.find(p => p.plan_name === 'Investor Premium'),
        },
        cofounder: {
          free: plans.find(p => p.plan_name === 'Co-Founder Free'),
          plus: plans.find(p => p.plan_name === 'Co-Founder Plus'),
        }
      };
      
      setAllPlans(organized);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan, billingCycle) => {
    if (!plan) return '$0';
    if (plan.monthly_price_usd === 0) return '$0';
    const price = billingCycle === 'monthly' 
      ? plan.monthly_price_usd 
      : Math.round(plan.yearly_price_usd / 12);
    return `$${price}`;
  };

  const BillingToggleDark = ({ billing, setBilling }) => (
    <div className="inline-flex items-center gap-2 bg-slate-700 p-1.5 rounded-lg border border-slate-600">
      <button
        onClick={() => setBilling('monthly')}
        className={`px-4 py-1.5 rounded-md transition-all text-sm ${
          billing === 'monthly' ? 'bg-blue-500 text-white font-semibold' : 'text-slate-300'
        }`}
      >
        Monthly
      </button>
      <button
        onClick={() => setBilling('annual')}
        className={`px-4 py-1.5 rounded-md transition-all flex items-center gap-2 text-sm ${
          billing === 'annual' ? 'bg-blue-500 text-white font-semibold' : 'text-slate-300'
        }`}
      >
        Annual
        <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">-20%</span>
      </button>
    </div>
  );

  const BillingToggleLight = ({ billing, setBilling }) => (
    <div className="inline-flex items-center gap-2 bg-slate-200 p-1.5 rounded-lg border border-slate-300">
      <button
        onClick={() => setBilling('monthly')}
        className={`px-4 py-1.5 rounded-md transition-all text-sm ${
          billing === 'monthly' ? 'bg-blue-600 text-white font-semibold' : 'text-blue-700'
        }`}
      >
        Monthly
      </button>
      <button
        onClick={() => setBilling('annual')}
        className={`px-4 py-1.5 rounded-md transition-all flex items-center gap-2 text-sm ${
          billing === 'annual' ? 'bg-blue-600 text-white font-semibold' : 'text-blue-700'
        }`}
      >
        Annual
        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">-20%</span>
      </button>
    </div>
  );

  if (loading || !allPlans) {
    return (
      <div className="min-h-screen bg-slate-800 flex items-center justify-center">
        <div className="text-slate-300">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <PublicNav currentPage="pricing" />

      {/* Section 1: Hero - Light (below dark nav) */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Choose Your Plan</h1>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-4 leading-relaxed">Pay for access, filtering, and time saved—not capital outcomes. SeedSynch earns from subscriptions, not from deals.</p>
          <p className="text-sm text-slate-600 max-w-xl mx-auto">We do not take commission, carry, or success fees from any partnerships made. Your subscription is your only cost.</p>
        </div>
      </section>

      {/* Section 2: Founder Plans - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-4">
              FOR FOUNDERS
            </div>
            <p className="text-slate-300 mb-2 text-sm">Discover investors. Access co-founder matching. Start relevant conversations.</p>
            <p className="text-slate-400 text-xs mb-4">Curated investor and co-founder discovery based on your preferences.</p>
            <BillingToggleDark billing={founderBilling} setBilling={setFounderBilling} />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Founder Free */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 hover:border-slate-500 transition-all">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{allPlans.founder.free?.plan_name || 'Founder Free'}</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-blue-400">$0</span>
                  <span className="text-slate-400 text-sm">forever</span>
                </div>
                <p className="text-slate-400 text-xs">Explore the platform</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Profile creation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">5 investor previews/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">5 match suggestions/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="text-slate-500 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-500">Messaging</span>
                </li>
              </ul>
            </Card>

            {/* Founder Pro */}
            <Card className="bg-slate-700/50 border-blue-500 p-6 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Zap size={12} />
                  POPULAR
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{allPlans.founder.pro?.plan_name || 'Founder Pro'}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-blue-400">{getPrice(allPlans.founder.pro, founderBilling)}</span>
                  <span className="text-slate-400 text-sm">/mo</span>
                </div>
                {founderBilling === 'annual' && (
                  <div className="text-xs text-green-400 mb-2">2 months free</div>
                )}
                <p className="text-slate-400 text-xs">Full discovery & communication</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Everything in Free</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Co-founder search</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Direct messaging</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Unlimited investor matches</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Pitch deck upload & NDA</span>
                </li>
              </ul>
            </Card>

            {/* Founder Elite */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 hover:border-slate-500 transition-all relative">
              <div className="absolute top-4 right-4">
                <Crown className="text-blue-400" size={18} />
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{allPlans.founder.elite?.plan_name || 'Founder Elite'}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-blue-400">{getPrice(allPlans.founder.elite, founderBilling)}</span>
                  <span className="text-slate-400 text-sm">/mo</span>
                </div>
                {founderBilling === 'annual' && (
                  <div className="text-xs text-green-400 mb-2">2 months free</div>
                )}
                <p className="text-slate-400 text-xs">Enhanced visibility & introductions</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Everything in Pro</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Featured visibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Curated investor intros</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Pitch deck feedback</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Centered CTA for Founders */}
          <div className="flex justify-center">
            <WaitlistCTA 
              variant="founder"
              theme="dark"
              size="lg"
              onClick={() => goToWaitlist('founder')}
              testId="founder-section-cta"
            />
          </div>
        </div>
      </section>

      {/* Section 3: Investor Plans - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-blue-600/10 text-blue-600 rounded-full text-sm font-medium mb-4">
              FOR INVESTORS
            </div>
            <p className="text-slate-700 mb-4 text-sm">Discover ventures that match your criteria. Filter by industry, stage, and structure preferences.</p>
            <BillingToggleLight billing={investorBilling} setBilling={setInvestorBilling} />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Investor Free */}
            <Card className="bg-white border-slate-200 p-6 hover:border-slate-300 transition-all">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{allPlans.investor.free?.plan_name || 'Investor Free'}</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-blue-600">$0</span>
                  <span className="text-slate-600 text-sm">forever</span>
                </div>
                <p className="text-slate-600 text-xs">Browse and save ventures</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Limited browsing (15/month)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Save to watchlist</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">1 pitch preview/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">1 message/month</span>
                </li>
              </ul>
            </Card>

            {/* Investor Pro */}
            <Card className="bg-white border-blue-500 p-6 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Zap size={12} />
                  POPULAR
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{allPlans.investor.pro?.plan_name || 'Investor Pro'}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-blue-600">{getPrice(allPlans.investor.pro, investorBilling)}</span>
                  <span className="text-slate-600 text-sm">/mo</span>
                </div>
                {investorBilling === 'annual' && (
                  <div className="text-xs text-green-700 mb-2">2 months free</div>
                )}
                <p className="text-slate-600 text-xs">Full access to venture discovery</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Unlimited discovery</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Direct messaging</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">NDA pitch deck access</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Advanced filters</span>
                </li>
              </ul>
            </Card>

            {/* Investor Premium */}
            <Card className="bg-white border-slate-200 p-6 hover:border-slate-300 transition-all relative">
              <div className="absolute top-4 right-4">
                <Star className="text-blue-500" size={18} />
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{allPlans.investor.premium?.plan_name || 'Investor Premium'}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-blue-600">{getPrice(allPlans.investor.premium, investorBilling)}</span>
                  <span className="text-slate-600 text-sm">/mo</span>
                </div>
                {investorBilling === 'annual' && (
                  <div className="text-xs text-green-700 mb-2">2 months free</div>
                )}
                <p className="text-slate-600 text-xs">Priority access & curation</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Everything in Pro</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Curated deal flow</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">48h early access</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-700">Dedicated account manager</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Centered CTA for Investors */}
          <div className="flex justify-center">
            <WaitlistCTA 
              variant="investor"
              theme="light"
              size="lg"
              onClick={() => goToWaitlist('investor')}
              testId="investor-section-cta"
            />
          </div>
        </div>
      </section>

      {/* Section 4: Co-Founder Plans - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-4">
              FOR CO-FOUNDERS
            </div>
            <p className="text-slate-300 mb-4 text-sm">Find founders and ventures that match your skills and expectations.</p>
            <BillingToggleDark billing={cofounderBilling} setBilling={setCofounderBilling} />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Co-Founder Free */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 hover:border-slate-500 transition-all">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{allPlans.cofounder.free?.plan_name || 'Co-Founder Free'}</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-blue-400">$0</span>
                  <span className="text-slate-400 text-sm">forever</span>
                </div>
                <p className="text-slate-400 text-xs">Explore co-founder opportunities</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Browse profiles</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">5 matches/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="text-slate-500 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-500">Unlimited messaging</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="text-slate-500 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-500">Visibility boost</span>
                </li>
              </ul>
            </Card>

            {/* Co-Founder Plus */}
            <Card className="bg-slate-700/50 border-blue-500 p-6 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Zap size={12} />
                  BEST VALUE
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">{allPlans.cofounder.plus?.plan_name || 'Co-Founder Plus'}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-blue-400">{getPrice(allPlans.cofounder.plus, cofounderBilling)}</span>
                  <span className="text-slate-400 text-sm">/mo</span>
                </div>
                {cofounderBilling === 'annual' && (
                  <div className="text-xs text-green-400 mb-2">2 months free</div>
                )}
                <p className="text-slate-400 text-xs">Serious about finding a partner</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Unlimited matching</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Unlimited messaging</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Visibility boost</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-slate-300">Advanced filters</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Centered CTA for Co-founders */}
          <div className="flex justify-center mt-10">
            <WaitlistCTA 
              variant="founder"
              theme="dark"
              size="lg"
              onClick={() => goToWaitlist('founder')}
              testId="cofounder-section-cta"
            />
          </div>
        </div>
      </section>

      {/* Section 5: FAQ CTA - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Have Questions?</h2>
          <p className="text-slate-700 mb-6 text-sm">Check out our FAQ or contact us for more information</p>
          <Link to="/faq">
            <Button className="btn-secondary">View FAQ</Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
